import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Portal } from "@headlessui/react";
import { useField } from "formik";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CheckIcon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { computePanelPosition } from "../../utils/pickers/PMEntityPicker";
import { isRecipientEmail, loadRecipientOptions, recipientKey, splitRecipientEmails, uniqueRecipients } from "./recipientOptions";
import "./SendRecipientPicker.css";

const EMPTY = [];
const groupFor = (item) => item.group || (
  item.key === "me" || item.key === "internal_project_leader" ? "team"
    : ["customer", "contact", "supplier", "project", "agreement"].includes(item.key) ? "customer" : "other"
);

/** Claude Design E-postvelger, implemented with React and Promang tokens. */
export function SendRecipientPicker({
  label, options = EMPTY, value = EMPTY, onChange, onDraftChange,
  multiple = true, customerId, disabled = false, error, autoFocus = false,
  canSelect, loadOptions = loadRecipientOptions,
}) {
  const { t } = useTranslation();
  const companyId = useSelector((state) => state.session?.currentCompany?.id);
  const userId = useSelector((state) => state.session?.currentUser?.id);
  const id = useId();
  const rootRef = useRef(null);
  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [invalid, setInvalid] = useState("");
  const [active, setActive] = useState(-1);
  const [reload, setReload] = useState(0);
  const [position, setPosition] = useState(null);
  const [result, setResult] = useState(null);
  const context = `${companyId}:${userId}:${customerId}`;
  const current = result?.context === context && result?.query === query && result?.reload === reload;
  const loading = open && Boolean(companyId) && !current;
  const directory = current ? result.items : EMPTY;
  const known = uniqueRecipients([...options.filter((item) => item.email && !item.disabled), ...directory]);
  const selected = uniqueRecipients(value);
  const filtered = known.filter((item) => `${item.name || ""} ${item.email}`.toLowerCase().includes(query.trim().toLowerCase()));
  const groups = ["customer", "team", "other"].map((key) => ({ key, items: filtered.filter((item) => groupFor(item) === key) })).filter((group) => group.items.length);
  const showCustom = isRecipientEmail(query) && !known.some((item) => recipientKey(item.email) === recipientKey(query)) && !selected.some((item) => recipientKey(item.email) === recipientKey(query));
  const flat = [...(showCustom ? [{ email: query.trim(), custom: true }] : []), ...groups.flatMap((group) => group.items)];
  const visibleError = invalid || error;
  const latest = useRef({});

  const setDraft = (text) => {
    setQuery(text);
    onDraftChange?.(text);
    setActive(-1);
  };
  const emit = (next) => { onChange?.(uniqueRecipients(next)); };
  const commit = (raw) => {
    if (disabled) return false;
    const parts = splitRecipientEmails(raw);
    if (!parts.length) return true;
    const additions = uniqueRecipients(parts.filter(isRecipientEmail).map((email) => known.find((row) => recipientKey(row.email) === recipientKey(email)) || { email: recipientKey(email), name: "", custom: true, group: "other" }));
    if (!multiple && (parts.length > 1)) {
      setDraft(raw);
      setInvalid(t("recipients.singleOnly"));
      return false;
    }
    let next = multiple ? [...selected] : [];
    const bad = parts.filter((email) => !isRecipientEmail(email));
    additions.forEach((item) => {
      if (next.some((row) => recipientKey(row.email) === recipientKey(item.email))) return;
      if (canSelect && !canSelect(item, next)) bad.push(item.email);
      else next.push(item);
    });
    if (additions.length && (!bad.length || multiple)) emit(next);
    setDraft(bad.join(", "));
    setInvalid(bad.length ? t("recipients.invalid", { value: bad.join(", ") }) : "");
    return !bad.length;
  };
  const choose = (item) => {
    if (disabled) return;
    if (item.custom && showCustom && recipientKey(item.email) === recipientKey(query)) { commit(query); return; }
    const exists = selected.some((row) => recipientKey(row.email) === recipientKey(item.email));
    if (!exists && canSelect && !canSelect(item, selected)) { setInvalid(t("recipients.extraLimit")); return; }
    emit(exists ? selected.filter((row) => recipientKey(row.email) !== recipientKey(item.email)) : multiple ? [...selected, item] : [item]);
    setDraft(""); setInvalid("");
    if (!multiple) setOpen(false);
    inputRef.current?.focus();
  };
  latest.current = { query, commit, onDraftChange };

  useEffect(() => {
    if (!open || disabled || !companyId) return undefined;
    const controller = new AbortController();
    let alive = true;
    const timer = setTimeout(() => {
      Promise.resolve().then(() => loadOptions({ query: query.trim(), customerId, signal: controller.signal }))
        .then((data) => { if (alive) setResult({ ...data, context, query, reload }); })
        .catch(() => { if (alive) setResult({ items: [], failed: ["customer", "team", "other"], context, query, reload }); });
    }, query ? 200 : 0);
    return () => { alive = false; clearTimeout(timer); controller.abort(); };
  }, [open, disabled, companyId, customerId, context, query, reload, loadOptions]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => fieldRef.current && setPosition(computePanelPosition(fieldRef.current));
    place();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (fieldRef.current) observer?.observe(fieldRef.current);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    window.visualViewport?.addEventListener("resize", place);
    return () => { observer?.disconnect(); window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); window.visualViewport?.removeEventListener("resize", place); };
  }, [open, selected.length]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event) => {
      if (rootRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) return;
      if (latest.current.query.trim()) latest.current.commit(latest.current.query);
      setOpen(false);
    };
    document.addEventListener("mousedown", outside, true);
    document.addEventListener("focusin", outside, true);
    return () => { document.removeEventListener("mousedown", outside, true); document.removeEventListener("focusin", outside, true); };
  }, [open]);
  useEffect(() => {
    panelRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [active]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  const previousContext = useRef(context);
  useEffect(() => {
    if (previousContext.current === context) return;
    previousContext.current = context;
    setOpen(false); setResult(null); setQuery(""); setInvalid("");
    latest.current.onDraftChange?.("");
  }, [context]);

  const onKeyDown = (event) => {
    if (event.nativeEvent?.isComposing || disabled) return;
    if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); }
    else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault(); setOpen(true);
      setActive((index) => flat.length ? (index + (event.key === "ArrowDown" ? 1 : -1) + flat.length) % flat.length : -1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (open && active >= 0 && flat[active]) choose(flat[active]);
      else if (query.trim()) commit(query);
      else setOpen(true);
    } else if ((event.key === "," || event.key === ";") && query.trim()) { event.preventDefault(); commit(query); }
    else if (event.key === "Tab" && query.trim()) { commit(query); setOpen(false); }
    else if (event.key === "Backspace" && !query && selected.length) emit(selected.slice(0, -1));
  };

  const row = (item) => {
    const index = flat.indexOf(item);
    const checked = selected.some((option) => recipientKey(option.email) === recipientKey(item.email));
    return <div key={recipientKey(item.email)} id={`${id}-option-${index}`} data-index={index} role="option" aria-selected={checked}
      className={`pm-recipient-option${checked ? " is-selected" : ""}${index === active ? " is-active" : ""}`}
      onMouseEnter={() => setActive(index)} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(item)}>
      <span className="pm-recipient-option__text">{item.custom ? t("recipients.add", { email: item.email }) : <>{item.name && <>{item.name} — </>}{item.email}</>}</span>
      {(item.channel === "private_email" || item.label) && (
        <span className="pm-recipient-role">
          {item.channel === "private_email" ? t("recipients.channel.private_email") : item.label}
        </span>
      )}
      <span className="pm-recipient-check" aria-hidden="true">{checked ? <CheckIcon /> : item.custom ? "+" : null}</span>
    </div>;
  };
  return <div className="pm-recipient" ref={rootRef} onKeyDown={(event) => { if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); inputRef.current?.focus(); } }}>
    <div className="pm-recipient-label"><label htmlFor={`${id}-input`}>{label || t("recipients.label")}</label>{selected.length > 1 && <span>{t("recipients.count", { count: selected.length })}</span>}</div>
    <div ref={fieldRef} className="pm-recipient-field" data-invalid={!!visibleError} data-disabled={disabled} onClick={(event) => { if (!disabled && !event.target.closest("button")) inputRef.current?.focus(); }}>
      {selected.map((item) => <span className="pm-recipient-chip" key={recipientKey(item.email)}>
        <span title={item.email}>{item.name ? `${item.name} — ${item.email}` : item.email}</span>
        {item.custom && <small>{t("recipients.manual")}</small>}
        <button type="button" disabled={disabled} aria-label={t("recipients.remove", { email: item.email })} onClick={() => { emit(selected.filter((row) => recipientKey(row.email) !== recipientKey(item.email))); inputRef.current?.focus(); }}><XMarkIcon aria-hidden="true" /></button>
      </span>)}
      <input ref={inputRef} id={`${id}-input`} role="combobox" aria-expanded={open} aria-controls={open ? `${id}-list` : undefined} aria-autocomplete="list"
        aria-activedescendant={open && flat[active] ? `${id}-option-${active}` : undefined} aria-invalid={!!visibleError} aria-describedby={`${id}-help`}
        autoFocus={autoFocus} disabled={disabled} autoComplete="off" spellCheck={false} value={query}
        placeholder={t(selected.length ? "recipients.placeholderMore" : "recipients.placeholder")}
        onChange={(event) => { setDraft(event.target.value); setInvalid(""); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
        onPaste={(event) => { const text = event.clipboardData.getData("text"); if (/[,;\s]/.test(text.trim())) { event.preventDefault(); commit(query + " " + text); } }} />
      <button type="button" className="pm-recipient-toggle" disabled={disabled} aria-label={t("recipients.showContacts")} aria-expanded={open}
        onMouseDown={(event) => event.preventDefault()} onClick={() => { inputRef.current?.focus(); setOpen(!open); }}><ChevronDownIcon aria-hidden="true" /></button>
    </div>
    <p id={`${id}-help`} role={visibleError ? "alert" : undefined} className={visibleError ? "pm-field-error" : open ? "sr-only" : "pm-help"}>{visibleError || t(multiple ? "recipients.help" : "recipients.singleHelp")}</p>
    {open && !disabled && <Portal><div ref={panelRef} className="pm-recipient-panel" style={{ ...position, visibility: position ? "visible" : "hidden" }}>
      <div className="pm-recipient-list" id={`${id}-list`} role="listbox" aria-label={label || t("recipients.label")} aria-multiselectable={multiple} aria-busy={loading}>
        {showCustom && row(flat[0])}
        {groups.map((group) => <div key={group.key} role="group" aria-label={t(`recipients.group.${group.key}`)}>
          <div className="pm-recipient-group" aria-hidden="true"><span>{t(`recipients.group.${group.key}`)}</span><span>{group.items.filter((item) => selected.some((s) => recipientKey(s.email) === recipientKey(item.email))).length || ""}</span></div>
          {group.items.map(row)}
        </div>)}
      </div>
      {loading && <div className="pm-recipient-state" role="status"><span className="pls-skel" style={{ display: "block", height: 12, width: "65%" }} /><span className="sr-only">{t("recipients.loading")}</span></div>}
      {current && result.failed.length > 0 && <div className="pm-recipient-state" role="alert">{t("recipients.loadFailed")} <button type="button" className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => setReload((v) => v + 1)}>{t("recipients.retry")}</button></div>}
      {!loading && !flat.length && <p className="pm-recipient-state" role="status">{t("recipients.empty")}</p>}
      {current && result.hasMore && <p className="pm-recipient-state">{t("recipients.moreResults")}</p>}
      <div className="pm-recipient-footer"><span>{t("recipients.keyMove")}</span><span>{t("recipients.keySelect")}</span><span>{t("recipients.keyClose")}</span></div>
    </div></Portal>}
  </div>;
}

/** Formik bridge preserves existing payload fields and validates uncommitted input. */
export function FormikSendRecipientPicker({ formik, emailField = "email", nameField = "name", valueField, options = EMPTY, multiple = false, onSelect, ...props }) {
  const { t } = useTranslation();
  const draftRef = useRef("");
  const [, draftMeta, draftHelpers] = useField({
    name: `_recipientDraft_${emailField}`,
    validate: () => draftRef.current.trim() ? t("recipients.finishInput") : undefined,
  });
  const cached = useRef([]);
  const known = [...options, ...cached.current];
  const value = valueField ? formik.values[valueField] || [] : splitRecipientEmails(formik.values[emailField]).map((email) => known.find((row) => recipientKey(row.email) === recipientKey(email)) || { email, name: splitRecipientEmails(formik.values[emailField]).length === 1 ? formik.values[nameField] : "", custom: true });
  const onChange = (rows) => {
    cached.current = rows;
    formik.setValues((values) => ({ ...values,
      ...(valueField ? { [valueField]: rows } : { [emailField]: rows.map((row) => row.email).join(", "), [nameField]: rows.map((row) => row.name).filter(Boolean).join(", ") }),
    }));
    const added = rows.find((row) => !value.some((old) => recipientKey(old.email) === recipientKey(row.email)));
    if (added) onSelect?.(added);
  };
  const setDraft = useCallback((text) => {
    draftRef.current = text;
    draftHelpers.setError(undefined);
  }, [draftHelpers]);
  return <SendRecipientPicker {...props} options={options} value={value} multiple={multiple} onChange={onChange} onDraftChange={setDraft}
    disabled={formik.isSubmitting || props.disabled}
    error={props.error || ((draftMeta.touched || formik.submitCount) && draftMeta.error) || ((formik.touched[valueField || emailField] || formik.submitCount) && formik.errors[valueField || emailField])} />;
}

/** SMS receiver shortcuts stay separate from the email-only picker. */
export function PhoneRecipientShortcuts({ label, options, onSelect }) {
  return <fieldset><legend className="pm-label mb-2">{label}</legend><div className="flex flex-wrap gap-2">{options.filter((option) => option.mobile).map((option) => <button type="button" key={option.key} disabled={option.disabled} className="pm-btn pm-btn--secondary pm-btn--sm" onClick={() => onSelect(option)}>{option.label}</button>)}</div></fieldset>;
}

export function SendDialogActions({ onCancel, onSubmit, submitting, cancelLabel, sendLabel, sendingLabel }) {
  return <div className="pm-form-footer flex justify-end gap-3">
    <button type="button" onClick={onCancel} disabled={submitting} className="pm-btn pm-btn--secondary">{cancelLabel}</button>
    <button type="button" onClick={onSubmit} disabled={submitting} aria-busy={submitting} className="pm-btn pm-btn--primary">{submitting && <span className="pm-btn__spinner" aria-hidden="true" />}{submitting ? sendingLabel : sendLabel}</button>
  </div>;
}
